import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, UserPlus, Users, FileText, Loader2, ShieldCheck } from "lucide-react";
import { api, fmtPKR, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal for searching the central guarantor pool.
 *
 * Props
 *   open, onOpenChange    dialog state
 *   onPick(guarantor)     selected — caller maps to its CustomerForm slot
 *   onCreateNew()         user clicked "Add New" → caller resets the slot
 *                         to an empty inline form
 *   slotLabel             "Guarantor 1" / "Guarantor 2" — header context
 */
export default function GuarantorPickerDialog({
  open, onOpenChange, onPick, onCreateNew, slotLabel = "Guarantor",
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ(""); setDebounced(""); setRows([]);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/guarantors", { params: debounced ? { q: debounced } : {} })
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, debounced]);

  const initials = (name = "") => name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";

  const pick = (g) => {
    onPick?.(g);
    onOpenChange(false);
  };

  const empty = !loading && rows.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col bg-white text-slate-900"
        data-testid="guarantor-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Select {slotLabel}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Apne purane guarantors mein se choose karein — ya neecha "Add New" se ek naya add karein.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile, ya CNIC…"
            className="pl-9"
            autoFocus
            data-testid="guarantor-picker-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          )}

          {empty && (
            <div className="text-center py-10 text-sm text-slate-500" data-testid="guarantor-picker-empty">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              {debounced ? (
                <>No guarantor matched <span className="font-semibold">"{debounced}"</span>.</>
              ) : (
                <>Abhi tak koi guarantor record nahi hai is shop mein.</>
              )}
              <div className="mt-4">
                <Button
                  type="button"
                  onClick={() => { onCreateNew?.(); onOpenChange(false); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="guarantor-picker-add-new-empty"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> Add New Guarantor
                </Button>
              </div>
            </div>
          )}

          {!loading && rows.length > 0 && (
            <div className="space-y-2">
              {rows.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => pick(g)}
                  className="w-full text-left rounded-md border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 transition p-3 flex items-center gap-3"
                  data-testid={`guarantor-picker-row-${g.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center text-slate-500 font-bold flex-shrink-0">
                    {g.documents?.photo ? (
                      <img src={g.documents.photo} alt={g.name} className="w-full h-full object-cover" />
                    ) : (
                      initials(g.name)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-slate-900 truncate">{g.name}</div>
                      {g.relationship && (
                        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{g.relationship}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 tabular">
                      {g.cnic || "no CNIC"} · {g.mobile || "no mobile"}
                    </div>
                    {g.address && <div className="text-[11px] text-slate-400 truncate">{g.address}</div>}
                  </div>
                  <div className="text-right text-xs space-y-0.5 flex-shrink-0 min-w-[110px]">
                    <div className="flex items-center justify-end gap-1 text-slate-600">
                      <Users className="w-3 h-3" /> <span className="tabular font-bold">{g.customers_linked || 0}</span> customers
                    </div>
                    <div className="flex items-center justify-end gap-1 text-emerald-700">
                      <FileText className="w-3 h-3" /> <span className="tabular font-bold">{g.active_loans || 0}</span> active loans
                    </div>
                    {g.outstanding_amount > 0 && (
                      <div className="text-amber-700 tabular text-[10px]">
                        Outstanding: {fmtPKR(g.outstanding_amount)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!empty && (
          <div className="border-t border-slate-100 pt-3 flex justify-between gap-2">
            <div className="text-xs text-slate-500 self-center">
              {rows.length} guarantor{rows.length === 1 ? "" : "s"} found
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onCreateNew?.(); onOpenChange(false); }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="guarantor-picker-add-new"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Add New Guarantor
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
